import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Editor } from '@tinymce/tinymce-react';
import config from '../../config/config';
import { useSnackbar } from 'notistack';

interface CmsPage {
  id: number;
  title: string;
  slug: string;
  content: string;
  show_in_navbar: boolean;
  order_index: number;
}

const CmsManager: React.FC = () => {
  const [pages, setPages] = useState<CmsPage[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPage, setSelectedPage] = useState<CmsPage | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    showInNavbar: false,
    orderIndex: 0
  });
  const { enqueueSnackbar } = useSnackbar();

  const fetchPages = useCallback(async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/cms/pages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Kunde inte hämta sidorna');
      const data = await response.json();
      setPages(data);
    } catch (error) {
      enqueueSnackbar('Ett fel uppstod när sidorna skulle hämtas', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const handleOpenDialog = (page?: CmsPage) => {
    if (page) {
      setSelectedPage(page);
      setFormData({
        title: page.title,
        slug: page.slug,
        content: page.content,
        showInNavbar: page.show_in_navbar,
        orderIndex: page.order_index
      });
    } else {
      setSelectedPage(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        showInNavbar: false,
        orderIndex: pages.length
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPage(null);
  };

  const handleSubmit = async () => {
    try {
      const url = selectedPage
        ? `${config.api.baseUrl}/cms/pages/${selectedPage.id}`
        : `${config.api.baseUrl}/cms/pages`;
      
      const method = selectedPage ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: formData.title,
          slug: formData.slug,
          content: formData.content,
          showInNavbar: formData.showInNavbar,
          orderIndex: formData.orderIndex
        })
      });

      if (!response.ok) throw new Error('Kunde inte spara sidan');
      
      enqueueSnackbar(selectedPage ? 'Sidan har uppdaterats' : 'Sidan har skapats', { variant: 'success' });
      handleCloseDialog();
      fetchPages();
    } catch (error) {
      enqueueSnackbar('Ett fel uppstod när sidan skulle sparas', { variant: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna sida?')) return;

    try {
      const response = await fetch(`${config.api.baseUrl}/cms/pages/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Kunde inte ta bort sidan');
      
      enqueueSnackbar('Sidan har tagits bort', { variant: 'success' });
      fetchPages();
    } catch (error) {
      enqueueSnackbar('Ett fel uppstod när sidan skulle tas bort', { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4">Sidhantering</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Skapa ny sida
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Titel</TableCell>
              <TableCell>URL-slug</TableCell>
              <TableCell>Visa i menyn</TableCell>
              <TableCell>Ordning</TableCell>
              <TableCell>Åtgärder</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pages.map((page) => (
              <TableRow key={page.id}>
                <TableCell>{page.title}</TableCell>
                <TableCell>{page.slug}</TableCell>
                <TableCell>{page.show_in_navbar ? 'Ja' : 'Nej'}</TableCell>
                <TableCell>{page.order_index}</TableCell>
                <TableCell>
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(page)}
                    sx={{ mr: 1 }}
                  >
                    Redigera
                  </Button>
                  <Button
                    startIcon={<DeleteIcon />}
                    color="error"
                    onClick={() => handleDelete(page.id)}
                  >
                    Ta bort
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedPage ? 'Redigera sida' : 'Skapa ny sida'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Titel"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
            />
            <TextField
              label="URL-slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              fullWidth
              helperText="Används i URL:en, t.ex. /sidor/min-sida"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.showInNavbar}
                  onChange={(e) => setFormData({ ...formData, showInNavbar: e.target.checked })}
                />
              }
              label="Visa i navigeringsmenyn"
            />
            <TextField
              label="Ordning i menyn"
              type="number"
              value={formData.orderIndex}
              onChange={(e) => setFormData({ ...formData, orderIndex: parseInt(e.target.value) })}
              fullWidth
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Innehåll</Typography>
              <Editor
                apiKey="your-tinymce-api-key"
                value={formData.content}
                onEditorChange={(content) => setFormData({ ...formData, content })}
                init={{
                  height: 500,
                  menubar: true,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Avbryt</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedPage ? 'Uppdatera' : 'Skapa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CmsManager; 